import React, { useState, useEffect } from 'react';
import { Tag, Plus, Clock, Users, Trash2, Power } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PromotionService } from '../services/promotionService';
import { Promotion } from '../types';
import { format } from 'date-fns';

export const Promotions: React.FC = () => {
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [code, setCode] = useState('');
    const [discountValue, setDiscountValue] = useState('20');
    const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
    const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await PromotionService.getPromotions();
            setPromotions(data);
        } catch (error) {
            console.error('Failed to load promotions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await PromotionService.createPromotion({
                title,
                description,
                code: code.toUpperCase(),
                discountValue: parseInt(discountValue),
                discountType,
                startDate: new Date(startDate),
                endDate: new Date(endDate),
            });
            setShowModal(false);
            loadData();
            // Reset form
            setTitle('');
            setDescription('');
            setCode('');
        } catch (error) {
            alert('Failed to create promotion');
        }
    };

    const toggleStatus = async (id: string, currentStatus: boolean) => {
        try {
            await PromotionService.updatePromotionStatus(id, !currentStatus);
            loadData();
        } catch (error) {
            alert('Failed to update status');
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Delete this promotion?')) {
            try {
                await PromotionService.deletePromotion(id);
                loadData();
            } catch (error) {
                alert('Failed to delete');
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Promotions & Offers</h1>
                    <p className="text-secondary-600">Create and manage discount codes and special deals</p>
                </div>
                <Button onClick={() => setShowModal(true)} className="mt-4 sm:mt-0 flex items-center">
                    <Plus className="h-4 w-4 mr-2" />
                    New Promotion
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {promotions.map((promo) => (
                        <Card key={promo.id} className={`overflow-hidden border-2 transition-all ${promo.isActive ? 'border-primary-100' : 'border-secondary-100 opacity-75'}`}>
                            <div className="p-5">
                                <div className="flex justify-between items-start mb-4">
                                    <div className={`p-2 rounded-lg ${promo.isActive ? 'bg-primary-50 text-primary-600' : 'bg-secondary-100 text-secondary-400'}`}>
                                        <Tag className="h-6 w-6" />
                                    </div>
                                    <div className="flex space-x-1">
                                        <button
                                            onClick={() => toggleStatus(promo.id, promo.isActive)}
                                            className={`p-2 rounded-md transition-colors ${promo.isActive ? 'text-green-600 hover:bg-green-50' : 'text-secondary-400 hover:bg-secondary-50'}`}
                                            title={promo.isActive ? "Deactivate" : "Activate"}
                                        >
                                            <Power className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(promo.id)}
                                            className="p-2 rounded-md text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h3 className="text-lg font-bold text-secondary-900">{promo.title}</h3>
                                    <p className="text-sm text-secondary-500 line-clamp-2">{promo.description}</p>
                                </div>

                                <div className="flex items-center justify-between py-3 px-4 bg-secondary-50 rounded-xl mb-4">
                                    <div>
                                        <p className="text-[10px] uppercase font-bold text-secondary-400">Promo Code</p>
                                        <p className="text-lg font-mono font-bold text-primary-600 tracking-wider transition-colors">{promo.code}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] uppercase font-bold text-secondary-400">Discount</p>
                                        <p className="text-lg font-bold text-secondary-900">
                                            {promo.discountValue}{promo.discountType === 'percentage' ? '%' : ' GHS'} OFF
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between text-xs text-secondary-500">
                                    <div className="flex items-center">
                                        <Clock className="h-3 w-3 mr-1" />
                                        <span>Until {format(promo.endDate, 'MMM d, yyyy')}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Users className="h-3 w-3 mr-1" />
                                        <span>{promo.usageCount} uses</span>
                                    </div>
                                </div>
                            </div>
                            <div className={`px-5 py-3 border-t text-xs font-bold text-center uppercase tracking-widest ${promo.isActive ? 'bg-primary-50 text-primary-600' : 'bg-secondary-50 text-secondary-400'}`}>
                                {promo.isActive ? 'Active Offer' : 'Paused'}
                            </div>
                        </Card>
                    ))}
                    {promotions.length === 0 && (
                        <Card className="col-span-full p-12 text-center text-secondary-500 border-dashed border-2">
                            <div className="mb-4 flex justify-center">
                                <Tag className="h-12 w-12 text-secondary-200" />
                            </div>
                            <h3 className="text-lg font-medium text-secondary-900">No promotions yet</h3>
                            <p className="mt-1">Create your first offer to attract more passengers!</p>
                            <Button variant="outline" className="mt-6" onClick={() => setShowModal(true)}>
                                <Plus className="h-4 w-4 mr-2" />
                                Add Promotion
                            </Button>
                        </Card>
                    )}
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">Create New Promotion</h2>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">Title</label>
                                <input
                                    required
                                    type="text"
                                    placeholder="e.g. Early Bird Special"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-secondary-700 mb-1">Description</label>
                                <textarea
                                    required
                                    rows={2}
                                    placeholder="e.g. Save 20% by booking 7 days in advance"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">Promo Code</label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="EARLY20"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg font-mono uppercase"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">Discount Type</label>
                                    <select
                                        value={discountType}
                                        onChange={(e) => setDiscountType(e.target.value as any)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="fixed">Fixed Amount (GHS)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">Discount Value</label>
                                    <input
                                        required
                                        type="number"
                                        value={discountValue}
                                        onChange={(e) => setDiscountValue(e.target.value)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-secondary-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        required
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <Button variant="outline" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
                                <Button type="submit">Create Offer</Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
};
